import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ClockIcon } from 'lucide-react'

import { ApiError } from '@/api/errors'
import { portalApi } from '@/api/portal'
import { EmptyState } from '@/components/empty-state'
import { PageHeader } from '@/components/page-header'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { msg } from '@/i18n'
import { formatDuration } from '@/lib/format'

export const Route = createFileRoute('/portal/time')({
  component: PortalTimePage,
})

function PortalTimePage() {
  const summary = useQuery({
    queryKey: ['portal', 'time'],
    queryFn: () => portalApi.timeSummary(),
    retry: false,
  })

  // Un 403 signifie que le client n'a pas activé la visibilité du temps.
  const forbidden = summary.error instanceof ApiError && summary.error.isForbidden
  const t = msg().portalRequests.time

  return (
    <div className="space-y-5">
      <PageHeader title={t.title} />

      {summary.isPending ? (
        <Skeleton className="h-48 w-full rounded-lg" />
      ) : forbidden ? (
        <EmptyState
          icon={ClockIcon}
          title={t.notSharedTitle}
          description={t.notSharedDescription}
        />
      ) : summary.isError ? (
        <EmptyState
          icon={ClockIcon}
          title={t.loadFailed}
          description={summary.error instanceof ApiError ? summary.error.message : t.retryLater}
        />
      ) : (
        <>
          <Card>
            <CardContent className="space-y-1 pt-5">
              <p className="text-xs font-medium text-muted-foreground">{t.total}</p>
              <p className="text-2xl font-semibold tabular-nums">
                {formatDuration(summary.data.total_seconds)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t.byRequest}</CardTitle>
            </CardHeader>
            <CardContent>
              {summary.data.by_task.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t.none}</p>
              ) : (
                <ul className="divide-y">
                  {summary.data.by_task.map((row) => (
                    <li key={row.task.id} className="flex items-center gap-2 py-2">
                      <code className="font-key shrink-0 text-muted-foreground">
                        {row.task.key}
                      </code>
                      <span className="min-w-0 flex-1 truncate text-sm">{row.task.title}</span>
                      <span className="text-sm font-medium tabular-nums">
                        {formatDuration(row.seconds)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
